import { useCallback, useEffect, useState } from "react";

import { hideAsync } from "expo-splash-screen";

import ColdStartTracker from "@/services/ColdStartTracker";

/**
 * This hook is used to hide the app-startup-only splash screen when the app is ready to use.
 * It is used in the root navigator.
 *
 * NOTE: all other splash screen use cases (when switching apps, due to inactivity, etc)
 * are provided by ui/components/splashScreen/SplashScreenProvider.
 * however both implementations do provide a visually identical splash screen (intentionally so)
 */
const useAppStartupSplashScreenManager = ({
  isLoadingCredentials,
  isRootNavigatorReady,
}: {
  isLoadingCredentials: boolean;
  isRootNavigatorReady: boolean;
}) => {
  const [hasHiddenSplashScreen, setHasHiddenSplashScreen] =
    useState<boolean>(false);

  const hideSplashScreen = useCallback(async () => {
    void hideAsync().then(() => {
      setHasHiddenSplashScreen(true);
      ColdStartTracker.complete();
    });
  }, []);

  useEffect(() => {
    if (hasHiddenSplashScreen) return;
    if (isLoadingCredentials || !isRootNavigatorReady) return;
    void hideSplashScreen();
  }, [
    isLoadingCredentials,
    isRootNavigatorReady,
    hasHiddenSplashScreen,
    hideSplashScreen,
  ]);

  return {
    hasHiddenSplashScreen,
  };
};

export default useAppStartupSplashScreenManager;
